import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { config } from '../config';
import { toast } from 'react-toastify';

const UserSettingsPage = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    website: '',
    bio: '',
    gender: 'prefer_not_to_say',
    instagram: '',
    tiktok: '',
    youtube: '',
    facebook: '',
    address: '',
    city: '',
    country: '',
    paymentDetails: '',
    facebookPixelId: '',
    facebookAccessToken: ''
  });
  
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const profile = await apiService.getAffiliateProfile();
      
      setFormData({
        username: profile.username || '',
        name: profile.name || profile.affiliateData?.name || '',
        website: profile.website || '',
        bio: profile.bio || '',
        gender: profile.gender || 'prefer_not_to_say',
        instagram: profile.affiliateData?.instagram || '',
        tiktok: profile.affiliateData?.tiktok || '',
        youtube: profile.affiliateData?.youtube || '',
        facebook: profile.affiliateData?.facebook || '',
        address: profile.affiliateData?.address || '',
        city: profile.affiliateData?.city || '',
        country: profile.affiliateData?.country || '',
        paymentDetails: profile.affiliateData?.paymentDetails || '',
        facebookPixelId: profile.affiliateData?.facebookPixelId || '',
        facebookAccessToken: profile.affiliateData?.facebookAccessToken || ''
      });
      
      // Set profile picture preview
      if (profile.profileIcon?.path) {
        const imageUrl = profile.profileIcon.path.startsWith('http') 
          ? profile.profileIcon.path 
          : `${config.SERVER_URL}${profile.profileIcon.path}`;
        setProfilePicturePreview(imageUrl);
      }
      
      // Update user context
      setUser({ ...user, ...profile });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile settings');
      toast.error('Failed to load profile settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    
    // Limit bio to 150 characters
    if (field === 'bio' && value.length > 150) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      toast.error('Image size should be less than 5MB');
      return;
    }

    setProfilePicture(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleProfilePictureUpload = async () => {
    if (!profilePicture) {
      toast.error('Please select a profile picture');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const result = await apiService.uploadProfilePicture(profilePicture);
      
      // Update preview with new URL
      if (result.fileUrl) {
        setProfilePicturePreview(result.fileUrl);
      }
      
      // Update user context
      if (result.profileIcon) {
        setUser({ ...user, profileIcon: result.profileIcon });
      }
      
      setProfilePicture(null);
      toast.success('Profile picture uploaded successfully');
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload profile picture';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const updateData = {
        username: formData.username || undefined,
        name: formData.name || undefined,
        website: formData.website || undefined,
        bio: formData.bio || undefined,
        gender: formData.gender || undefined,
        affiliateData: {
          name: formData.name,
          instagram: formData.instagram,
          tiktok: formData.tiktok,
          youtube: formData.youtube,
          facebook: formData.facebook,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          paymentDetails: formData.paymentDetails,
          facebookPixelId: formData.facebookPixelId,
          facebookAccessToken: formData.facebookAccessToken
        }
      };
      
      const updatedProfile = await apiService.updateAffiliateProfile(updateData);
      
      // Update user context
      setUser({ ...user, ...updatedProfile });
      
      setSuccess('Settings saved successfully');
      toast.success('Settings saved successfully');
    } catch (err) {
      console.error('Error saving settings:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save settings';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Logo and Title */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: '#000000'
          }}
        >
          Edit profile
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <img
            src="/log_big.png"
            alt="Boost Health Daily"
            style={{ 
              maxWidth: '140px', 
              height: 'auto'
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper 
        elevation={0} 
        sx={{ 
          p: 3,
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          mb: 3
        }}
      >
        {/* Profile Picture Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            src={profilePicturePreview}
            sx={{ width: 64, height: 64 }}
          >
            {!profilePicturePreview && <AccountCircleIcon sx={{ fontSize: 64 }} />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000', mb: 1 }}>
              Profile Picture
            </Typography>
          </Box>
          <Box>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="profile-picture-upload"
              type="file"
              onChange={handleProfilePictureChange}
            />
            <label htmlFor="profile-picture-upload">
              <Button
                variant="contained"
                component="span"
                size="small"
                disabled={uploading}
                sx={{
                  backgroundColor: '#1976d2',
                  color: '#ffffff',
                  textTransform: 'none',
                  borderRadius: 1
                }}
              >
                Change photo
              </Button>
            </label>
            {profilePicture && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleProfilePictureUpload}
                disabled={uploading}
                sx={{ ml: 1, textTransform: 'none' }}
              >
                {uploading ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Username Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
            Username
          </Typography>
          <TextField
            fullWidth
            placeholder="Username"
            value={formData.username}
            onChange={handleInputChange('username')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
                borderRadius: 1
              }
            }}
          />
        </Box>

        {/* Name Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
            Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Full Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
                borderRadius: 1
              }
            }}
          />
        </Box>
      </Paper>

      {/* Website Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
          Website
        </Typography>
        <TextField
          fullWidth
          placeholder="Website"
          value={formData.website}
          onChange={handleInputChange('website')}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f5f5f5',
              borderRadius: 1
            }
          }}
        />

      </Box>

      {/* Bio Section */}
      <Box sx={{ mb: 3, position: 'relative' }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
          Bio
        </Typography>
        <TextField
          fullWidth
          placeholder="Bio"
          value={formData.bio}
          onChange={handleInputChange('bio')}
          multiline
          rows={4}
          inputProps={{ maxLength: 150 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f5f5f5',
              borderRadius: 1
            }
          }}
        />
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#999999',
            position: 'absolute',
            bottom: 8,
            right: 12,
            pointerEvents: 'none'
          }}
        >
          {formData.bio.length}/150
        </Typography>
      </Box>

      {/* Gender Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
          Gender
        </Typography>
        <FormControl fullWidth>
          <Select
            value={formData.gender}
            onChange={handleInputChange('gender')}
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ color: '#999999', mt: 0.5, display: 'block' }}>
          This won't be part of your public profile.
        </Typography>
      </Box>

      {/* Social Media Handles */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
          Social Media
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
            instagram handle
          </Typography>
          <TextField
            fullWidth
            placeholder="Instagram handle"
            value={formData.instagram}
            onChange={handleInputChange('instagram')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
            tiktok handle
          </Typography>
          <TextField
            fullWidth
            placeholder="TikTok handle"
            value={formData.tiktok}
            onChange={handleInputChange('tiktok')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
            youtube channel
          </Typography>
          <TextField
            fullWidth
            placeholder="YouTube channel"
            value={formData.youtube}
            onChange={handleInputChange('youtube')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#000000' }}>
            Facebook page
          </Typography>
          <TextField
            fullWidth
            placeholder="Facebook page"
            value={formData.facebook}
            onChange={handleInputChange('facebook')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
        </Box>
      </Box>

      {/* Address Information Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
          Address Information
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Address"
            placeholder="Address"
            value={formData.address}
            onChange={handleInputChange('address')}
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="City"
            placeholder="City"
            value={formData.city}
            onChange={handleInputChange('city')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
          <TextField
            fullWidth
            label="Country"
            placeholder="Country"
            value={formData.country}
            onChange={handleInputChange('country')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
          />
        </Box>
      </Box>

      {/* Payment Information Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
          Payment Information
        </Typography>
        
        <TextField
          fullWidth
          label="Payment Details"
          placeholder="Enter your payment information (e.g., PayPal email, bank account details)"
          value={formData.paymentDetails}
          onChange={handleInputChange('paymentDetails')}
          multiline
          rows={4}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              borderRadius: 1
            }
          }}
          helperText="Enter your payment information (e.g., PayPal email, bank account details)"
        />
      </Box>

      {/* Facebook Information Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000000' }}>
          Facebook Information
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Facebook Pixel ID"
            placeholder="Facebook Pixel ID"
            value={formData.facebookPixelId}
            onChange={handleInputChange('facebookPixelId')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
            helperText="Your Facebook Pixel ID (required for Conversions API)"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Facebook Access Token"
            type="password"
            placeholder="Facebook Access Token"
            value={formData.facebookAccessToken}
            onChange={handleInputChange('facebookAccessToken')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1
              }
            }}
            helperText="Facebook System User Access Token (required for Conversions API)"
          />
        </Box>
      </Box>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={saving ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            textTransform: 'none',
            borderRadius: 1
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Container>
  );
};

export default UserSettingsPage;
